import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ArrowLeft, Briefcase, UserCheck, CheckCircle, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GestionarTrabajos = () => {
  const navigate = useNavigate();
  const [trabajos, setTrabajos] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [asignando, setAsignando] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [trabajosRes, tecnicosRes] = await Promise.all([
        axios.get(`${API}/trabajos`),
        axios.get(`${API}/admin/tecnicos`)
      ]);
      setTrabajos(trabajosRes.data);
      setTecnicos(tecnicosRes.data);
    } catch (err) {
      setError('Error al cargar datos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const asignarTecnico = async (trabajoId, tecnicoId) => {
    if (!tecnicoId) return;

    setAsignando({ ...asignando, [trabajoId]: true });
    setError('');
    setSuccess('');

    try {
      await axios.patch(
        `${API}/admin/trabajos/${trabajoId}/asignar?tecnico_id=${tecnicoId}`
      );
      setSuccess('Técnico asignado exitosamente');
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al asignar técnico');
    } finally {
      setAsignando({ ...asignando, [trabajoId]: false });
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'pendiente':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'en_progreso':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'completado':
        return 'bg-green-100 text-green-800 border-green-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getEstadoIcon = (estado) => {
    switch (estado) {
      case 'pendiente':
        return <Clock className="h-4 w-4" />;
      case 'en_progreso':
        return <Briefcase className="h-4 w-4" />;
      case 'completado':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const formatEstado = (estado) => {
    const estados = {
      pendiente: 'Pendiente',
      en_progreso: 'En Progreso',
      completado: 'Completado'
    };
    return estados[estado] || estado;
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A';
    return new Date(fecha).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6" data-testid="gestionar-trabajos">
      <div className="max-w-7xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4"
          data-testid="back-button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Volver al Panel
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              Gestionar Trabajos ({trabajos.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {success && (
              <Alert className="bg-green-50 border-green-200 mb-4">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">{success}</AlertDescription>
              </Alert>
            )}

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 mx-auto"></div>
                <p className="text-gray-600 mt-4">Cargando trabajos...</p>
              </div>
            ) : trabajos.length === 0 ? (
              <div className="text-center py-12">
                <Briefcase className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No hay trabajos registrados</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Trabajo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cliente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Lugar
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ciudad
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Técnico Asignado
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {trabajos.map((trabajo) => (
                      <tr key={trabajo.id} className="hover:bg-gray-50" data-testid={`trabajo-row-${trabajo.id}`}>
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <div className="text-sm font-medium text-gray-900">{trabajo.titulo}</div>
                            <div className="text-xs text-gray-500">{formatFecha(trabajo.fecha_creacion)}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {trabajo.cliente_nombre || 'No asignado'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {trabajo.lugar_nombre || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {trabajo.ciudad_nombre || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <Badge className={`${getEstadoColor(trabajo.estado)} flex items-center gap-1 w-fit`}>
                            {getEstadoIcon(trabajo.estado)}
                            {formatEstado(trabajo.estado)}
                          </Badge>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {trabajo.tecnico_asignado_nombre || (
                              <span className="text-gray-400 italic">Sin asignar</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Select
                              onValueChange={(value) => asignarTecnico(trabajo.id, value)}
                              disabled={asignando[trabajo.id]}
                            >
                              <SelectTrigger className="w-40" data-testid={`select-tecnico-${trabajo.id}`}>
                                <SelectValue placeholder="Asignar técnico" />
                              </SelectTrigger>
                              <SelectContent>
                                {tecnicos.map((tecnico) => (
                                  <SelectItem key={tecnico.id} value={tecnico.id}>
                                    {tecnico.nombre_completo}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/admin/trabajo/${trabajo.id}`)}
                            >
                              Ver
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GestionarTrabajos;
